import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { formatPt } from '../../../lib/format.js';
import type { PmoSupplyRateProject } from '../../../types/pmo-flow.js';

/**
 * プールの区分。開発リーダーの所属チームと、事業がメンズかの組で決まる。
 *
 * メンズを ACE / PDG の 2 つに束ねてチーム単位に散らさないのは、メンズが airCloset と
 * Platform のどちらの開発リーダーに付いても「メンズ事業にどれだけ枠を使っているか」を
 * 1 つの数字で見たいため。結果としてラベルは重複せず、区分とバーのセグメントが 1:1 になる。
 *
 * 並び順がそのままバーと凡例の並びになる。ACE 側をまとめて置き、PDG、最後に受け皿の その他。
 * @graph-connects none
 */
export const SUPPLY_RATE_SEGMENTS = [
  'ladiesAce',
  'mensAce',
  'platform',
  'pdg',
  'mensPdg',
  'other',
] as const;

/** プールの区分キー（`SUPPLY_RATE_SEGMENTS` の要素型）。 @graph-connects none */
export type SupplyRateSegmentKey = (typeof SUPPLY_RATE_SEGMENTS)[number];

/**
 * 在庫が 0 でも必ず描く区分。「その区分が空」と「区分そのものが無い」を見分けられるようにする。
 * メンズと その他 は該当が無ければ描かない（0 の枠が並ぶと内訳が読みにくくなるため）。
 * @graph-connects none
 */
const ALWAYS_SHOWN_SEGMENTS: ReadonlySet<SupplyRateSegmentKey> = new Set([
  'ladiesAce',
  'platform',
  'pdg',
]);

/**
 * 案件 1 件をどの区分に入れるか。区分は互いに排他で、どの案件も必ず 1 つに落ちる
 * （`other` が受け皿）。集計とドリルダウン一覧の pill が同じ判定を使うための SSOT。
 *
 * `other` はメンズかどうかで割らない。所属が解決できなかった案件の受け皿で、
 * さらに事業で割っても読み手の判断材料が増えないため。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件の開発リーダー所属チームと事業（メンズか）からプール区分を解決する
 * @graph-connects none
 */
export function toSupplyRateSegment(project: PmoSupplyRateProject): SupplyRateSegmentKey {
  switch (project.devLeaderTeam) {
    case 'aircloset':
      return project.isMensBusiness ? 'mensAce' : 'ladiesAce';
    case 'platform':
      return project.isMensBusiness ? 'mensAce' : 'platform';
    case 'pdg':
      return project.isMensBusiness ? 'mensPdg' : 'pdg';
    default:
      return 'other';
  }
}

/**
 * 区分 → 塗り色と、その上に載せる文字色。
 *
 * レディース(ACE) / プラットフォーム / PDG は KPI の実績pt内訳チャート
 * （`WeeklyPointTrendChart` / `AceMonthlyTrendChart`）と同じ配色で、画面をまたいで
 * 「赤＝airCloset / 水色＝Platform / 橙＝PDG」の対応を保つ。メンズは事業ラインの区分なので
 * チーム色と競合しない無彩色を ACE / PDG で共有する。
 *
 * ダークではベタ塗りにするため、塗りごとに読める文字色を対で持つ（水色・橙・灰は白文字だと
 * 沈むので黒寄り、暗い赤とメンズの濃灰は白文字）。
 *
 * メンズだけテーマトークン経由なのは、ライトでは黒に寄せたいがダークで同じ濃さにすると
 * 黒地に沈むため（`styles.css` の `--color-supply-mens`）。
 * @graph-connects none
 */
export const SUPPLY_RATE_PILL_TONES: Readonly<
  Record<SupplyRateSegmentKey, { fill: string; on: string }>
> = {
  ladiesAce: { fill: '#CB3030', on: '#ffffff' },
  mensAce: { fill: 'var(--color-supply-mens)', on: '#ffffff' },
  platform: { fill: '#40CEEE', on: '#111827' },
  pdg: { fill: '#FFB33F', on: '#111827' },
  mensPdg: { fill: 'var(--color-supply-mens)', on: '#ffffff' },
  other: { fill: '#9ca3af', on: '#111827' },
};

export interface SupplyRateTeamPillProps {
  /** 区分。塗りと文字色がこれで決まる */
  tone: SupplyRateSegmentKey;
  /** pill に出す文言 */
  label: string;
}

/**
 * 区分を表すカラー pill。内訳パネルの凡例と一覧の開発リーダー列で同じ見た目を使い、
 * 「上の内訳のどの色に属する行か」を色でそのまま辿れるようにする。
 *
 * 塗りの出し方はテーマで変わる（`styles.css` の `.supply-pill`）。ダークは地が暗く
 * 淡い塗りだと pill が地に溶けるためベタ塗り + 対の文字色、ライトは淡い塗り + 枠 +
 * ドットで色を伝えて文字はテーマトークンに任せる。どちらを当てるかは CSS 側の
 * `[data-theme='light']` が決め、ここは色だけを CSS 変数として渡す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 区分のカラーpill。区分色と対の文字色をCSS変数で渡し、ダークはベタ塗り/ライトは淡い塗り+枠+ドットをCSS側で出し分ける
 * @graph-connects none
 */
export function SupplyRateTeamPill({ tone, label }: SupplyRateTeamPillProps) {
  const { fill, on } = SUPPLY_RATE_PILL_TONES[tone];
  return (
    <span
      className="supply-pill inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium leading-none"
      style={{ '--supply-pill-fill': fill, '--supply-pill-on': on } as CSSProperties}
    >
      <span className="supply-pill-dot h-1.5 w-1.5 shrink-0 rounded-full" />
      {label}
    </span>
  );
}

/** 1 区分分のプール。区分は排他なので、全区分の pt を足すとプール全体になる。 */
export interface SupplyRateSegmentPool {
  key: SupplyRateSegmentKey;
  /** この区分の案件数 */
  projectCount: number;
  /** この区分のざっくり見積の合計 pt */
  points: number;
  /** プール全体 pt に占める割合（%、小数1桁）。全体が 0pt なら 0 */
  sharePercent: number;
}

/**
 * 明細を区分ごとに畳んでプールにする。
 *
 * 割合の分母は **件数ではなく pt 合計**。1 件 100pt と 5 件 10pt を同列に扱うと
 * 「枠をどれだけ埋めているか」という問いに答えられないため。全体が 0pt のときは
 * 0 除算を避けて全区分 0% にする。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 着手予定の案件明細を区分（レディース(ACE)/メンズ(ACE)/プラットフォーム/PDG/メンズ(PDG)/その他）別に畳み、件数・pt・pt占有率を返す
 * @graph-connects toSupplyRateSegment [calls] 案件の区分解決
 */
export function buildSegmentPools(
  projects: readonly PmoSupplyRateProject[],
): SupplyRateSegmentPool[] {
  const totalPoints = projects.reduce((sum, project) => sum + (project.countedPoint ?? 0), 0);

  return SUPPLY_RATE_SEGMENTS.map((key) => {
    const members = projects.filter((project) => toSupplyRateSegment(project) === key);
    const points = members.reduce((sum, project) => sum + (project.countedPoint ?? 0), 0);
    return {
      key,
      projectCount: members.length,
      points,
      sharePercent: totalPoints > 0 ? Math.round((points / totalPoints) * 1000) / 10 : 0,
    };
  }).filter((pool) => ALWAYS_SHOWN_SEGMENTS.has(pool.key) || pool.projectCount > 0);
}

interface SupplyRatePoolLabelProps {
  /** 区分。pill の色になる */
  tone: SupplyRateSegmentKey;
  /** pill に出す文言 */
  label: string;
  /** この区分の pt */
  points: number;
  /** pt の後ろに置く占有率・件数 */
  hint: string;
  /** この区分で絞り込み中か。押し込んだ見た目になる */
  selected: boolean;
  /** 在庫が 0 件で絞り込んでも意味がない区分か */
  disabled: boolean;
  /** クリック時に呼ぶ（絞り込みのトグル） */
  onClick: () => void;
  /** 読み上げ用の状態ラベル（区分名 + 件数 + pt） */
  ariaLabel: string;
}

/**
 * プール 1 区分の明細（pill + pt + 占有率/件数）。どの区分も同じ組版で出し、
 * 区分どうしの関係は **バー上の位置**で読ませる。
 *
 * バーのセグメントと同じ絞り込みを起こすボタンでもある。バーは高さ 10px で
 * 掴みにくく、キーボードからも辿れない（バー側はクリップされた装飾扱い）ため、
 * **この明細が絞り込みの正規の操作子**になる。押し込み状態は `aria-pressed` で持つ。
 *
 * 選択・ホバーの印は **pill の内側だけ**に出し、明細全体に背景やリングを掛けない。
 * 狭い区分の明細は隣の区分の領域まではみ出して描かれる（幅をセグメントに合わせる
 * 代償）ので、明細を囲む箱を出すと隣の区分まで一緒に選択されたように見えるため。
 *
 * 在庫 0 件の区分（常時表示する レディース(ACE)/プラットフォーム/PDG のうち該当なしのもの）は
 * 絞り込んでも空表になるだけなので押せなくする。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business プール1区分の明細ボタン。区分pill・pt・占有率/件数を全区分で同じ数字書式で縦に積み、クリックでその区分への絞り込みをトグルする
 * @graph-connects SupplyRateTeamPill [calls] 区分のカラーpill描画
 * @graph-connects formatPt [calls] pt値の整形
 */
function SupplyRatePoolLabel({
  tone,
  label,
  points,
  hint,
  selected,
  disabled,
  onClick,
  ariaLabel,
}: SupplyRatePoolLabelProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      aria-pressed={selected}
      aria-label={ariaLabel}
      onClick={onClick}
      className={`group block whitespace-nowrap text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-border-focus)] ${
        disabled ? 'cursor-default' : 'cursor-pointer'
      }`}
    >
      <span
        className={`inline-flex rounded-full ring-offset-1 ring-offset-[var(--color-bg-elevated)] ${
          selected
            ? 'ring-2 ring-[var(--color-border-focus)]'
            : disabled
              ? ''
              : 'group-hover:ring-1 group-hover:ring-[var(--color-border-hover)]'
        }`}
      >
        <SupplyRateTeamPill tone={tone} label={label} />
      </span>
      <div className="mt-1.5 flex items-baseline gap-1.5">
        <span
          className={`font-mono text-2xl font-bold tabular-nums ${
            selected ? 'text-[var(--color-accent-primary)]' : 'text-[var(--color-text-primary)]'
          }`}
        >
          {formatPt(points)}
        </span>
        <span className="text-xs text-[var(--color-text-muted)]">{hint}</span>
      </div>
    </button>
  );
}

export interface SupplyRateTeamBreakdownProps {
  /** 対象段の案件明細（着手予定段で使う）。絞り込み中でも常に全件を渡す */
  projects: readonly PmoSupplyRateProject[];
  /** 絞り込み中の区分。null なら全件表示 */
  selectedSegment: SupplyRateSegmentKey | null;
  /** 区分の選択が変わったときに呼ぶ。解除は null */
  onSelectSegment: (segment: SupplyRateSegmentKey | null) => void;
}

/**
 * 着手予定プールの区分別内訳。モーダルの一覧の上に置き、
 * 「この在庫はどこの枠を埋めているのか」を先に見せる。
 *
 * 判定材料は母票の 開発リーダー と 事業。開発リーダーには各チームの teamlead / BSE
 * （airCloset は Hiro、Platform は Yukito）かそのチームの DEV が入るため、
 * そのニックネームの所属チームがそのまま案件の所属になる。そのうえで事業がメンズの案件を
 * メンズ(ACE) / メンズ(PDG) へ分け、レディースとメンズを別々の pt として見せる。
 *
 * 100% 積み上げバーと数値を同じ枠に置くのは、pt の絶対値（＝どれだけ抱えているか）と
 * 占有率（＝どこに偏っているか）が別の問いで、片方だけでは判断できないため。
 *
 * バーのセグメントと数値はどちらも下の一覧をその区分だけに絞り込むボタンで、同じ区分を
 * 再度押すと解除される。**内訳自体は絞り込みの影響を受けない**（常に全件から畳む）。
 * 絞り込むたびに分母が変わると「全体のどこを見ているか」が分からなくなり、区分どうしの
 * 比較ができなくなるため。
 *
 * **バーの塗りは絞り込みで変えない**。バーは pt の占有率を色の面積で読ませる図で、
 * 選択外を淡くすると同じ色が「その区分の量」ではなく「選択されていないこと」を指すように
 * なり、図としての読み方が絞り込みの有無で変わってしまう。いまどの区分を見ているかは
 * 数値側の pill のリングと一覧の上の絞り込み行で示す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 着手予定案件モーダル冒頭の区分別プール内訳。レディース(ACE)/メンズ(ACE)/プラットフォーム/PDG/メンズ(PDG)ごとの件数・pt・pt占有率を100%積み上げバーと数値で示し、セグメントのクリックで一覧をその区分に絞り込む
 * @graph-connects buildSegmentPools [calls] 明細を区分別プールへ畳む
 * @graph-connects SupplyRatePoolLabel [calls] 区分1つ分の明細描画と絞り込みボタン
 */
export function SupplyRateTeamBreakdown({
  projects,
  selectedSegment,
  onSelectSegment,
}: SupplyRateTeamBreakdownProps) {
  const { t } = useTranslation();
  const pools = buildSegmentPools(projects);
  const label = (key: SupplyRateSegmentKey): string =>
    t(`kpi.pmoFlow.supplyRate.projectsModal.teamBreakdown.segment.${key}`);
  // 同じ区分をもう一度押したら解除。「絞り込みを外す」ために専用のボタンを探させない
  const toggle = (key: SupplyRateSegmentKey) => () => {
    onSelectSegment(selectedSegment === key ? null : key);
  };

  return (
    <div className="mb-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-4">
      <p className="text-xs text-[var(--color-text-muted)]">
        {t('kpi.pmoFlow.supplyRate.projectsModal.teamBreakdown.title')}
      </p>
      <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">
        {t('kpi.pmoFlow.supplyRate.projectsModal.teamBreakdown.filterHint')}
      </p>

      {/*
        バーと凡例は同じ幅計算で並ぶので、狭い画面で片方だけ潰れると桁がずれる。
        2 つまとめて 1 つの最小幅を持たせ、横スクロールも一緒に動かして整列を保つ
      */}
      <div className="mt-3 overflow-x-auto">
        {/*
          右端の区分の明細は、その区分の左端から右へはみ出す。バーと凡例の共通の親を
          右に空けておくことで、最後のラベルが切れずに収まる（バー側も同じ幅で縮むので
          桁は揃ったまま）
        */}
        <div className="min-w-[40rem] pr-32">
          {/*
            バーは読み上げ上は 1 枚の図のまま（role="img"）にし、中のボタンは
            tabIndex=-1 のマウス専用の当たり判定にする。同じ絞り込みはすぐ下の数値
            ボタンから辿れるので、キーボード/読み上げに区分ごとの重複したタブ位置を
            増やさない
          */}
          <div
            className="flex h-2.5 w-full gap-[3px] overflow-hidden rounded-full bg-[var(--color-bg-base)]"
            role="img"
            aria-label={pools
              .map((pool) =>
                t('kpi.pmoFlow.supplyRate.projectsModal.teamBreakdown.barSegment', {
                  segment: label(pool.key),
                  percent: pool.sharePercent,
                }),
              )
              .join(' / ')}
          >
            {pools.map((pool) => (
              <button
                key={pool.key}
                type="button"
                tabIndex={-1}
                disabled={pool.projectCount === 0}
                onClick={toggle(pool.key)}
                className={`h-full ${pool.projectCount === 0 ? 'cursor-default' : 'cursor-pointer'}`}
                style={{
                  width: `${String(pool.sharePercent)}%`,
                  backgroundColor: SUPPLY_RATE_PILL_TONES[pool.key].fill,
                }}
              />
            ))}
          </div>

          {/*
            バーと同じ幅・同じ隙間で並べ、各区分の明細がその区分の左端から始まるようにする。
            幅はセグメントに合わせるだけで、はみ出す分は右へ流す（min-width を付けると
            桁がずれてバーと対応しなくなるため、読みやすさより整列を優先する）
          */}
          <div className="mt-3 flex w-full gap-[3px]">
            {pools.map((pool) => (
              <div
                key={pool.key}
                className="min-w-0"
                style={{ width: `${String(pool.sharePercent)}%` }}
              >
                <SupplyRatePoolLabel
                  tone={pool.key}
                  label={label(pool.key)}
                  points={pool.points}
                  hint={t('kpi.pmoFlow.supplyRate.projectsModal.teamBreakdown.poolHint', {
                    percent: pool.sharePercent,
                    count: pool.projectCount,
                  })}
                  selected={selectedSegment === pool.key}
                  disabled={pool.projectCount === 0}
                  onClick={toggle(pool.key)}
                  ariaLabel={t(
                    'kpi.pmoFlow.supplyRate.projectsModal.teamBreakdown.filterSegmentLabel',
                    {
                      segment: label(pool.key),
                      count: pool.projectCount,
                      pt: formatPt(pool.points),
                    },
                  )}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
